/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useRef, type RefObject } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  Pressable,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { modalShellShared } from '../../../shared/modals/modalShellShared.styles';
import { TicketModalHeader } from '../../../shared/ticket/TicketModalHeader'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { spacing } from '../../../shared/theme/spacing';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { mastersModalStyles as styles } from './mastersModal.styles';

const WIDE_MODAL_BREAKPOINT = 768;

type Props = {
  visible: boolean;
  mode: 'create' | 'edit';
  entityKicker: string;
  entityName?: string | null;
  headerTitle: string;
  saving?: boolean;
  saveLabel: string;
  onSave: () => void;
  onClose: () => void;
  scrollContentRef?: RefObject<View | null>;
  children: React.ReactNode;
};

export function MasterFormModal({
  visible,
  mode,
  entityName,
  headerTitle,
  saving,
  saveLabel,
  onSave,
  onClose,
  scrollContentRef: scrollContentRefProp,
  children,
}: Props) {
  const { width, height: winH } = useWindowDimensions();
  const isWide = width >= WIDE_MODAL_BREAKPOINT;
  const isMobile = !isWide;
  const insets = useSafeAreaInsets();
  const modalBackdropPadding = useModalBackdropPadding();
  const internalScrollContentRef = useRef<View>(null);
  const scrollContentRef = scrollContentRefProp ?? internalScrollContentRef;

  if (!visible) return null;

  const heroSubtitle =
    mode === 'edit' && entityName?.trim()
      ? entityName.trim()
      : `Completá los datos.`;

  const handleClose = () => {
    if (!saving) onClose();
  };

  const card = (
    <Pressable
      style={[
        isMobile
          ? [styles.sheetCard, { maxHeight: winH * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }]
          : [
              styles.modalCard,
              modalShellShared.modalCardShell,
              isWide ? modalExperience.modalWidthComfortable : modalExperience.modalWidthStandard,
            ],
      ]}
      onPress={(event) => event.stopPropagation()}
    >
      {isMobile ? <View style={styles.grabber} /> : null}
      <TicketModalHeader
        kicker="Catálogo"
        title={headerTitle}
        subtitle={heroSubtitle}
        onClose={handleClose}
        compact={isMobile}
        closeDisabled={saving}
      />
      <View style={[styles.modalScroll, styles.modalScrollContent]}>
        <View ref={scrollContentRef} collapsable={false}>
          <View style={modalExperience.formPanel}>{children}</View>
        </View>
      </View>

      <View style={styles.modalActionsDock}>
        <View style={modalExperience.detailActionsGrid}>
          <TouchableOpacity
            style={[
              modalExperience.detailActionBtn,
              modalExperience.detailActionBtnPrimary,
              saving && { opacity: 0.6 },
            ]}
            disabled={saving}
            onPress={onSave}
            accessibilityRole="button"
            accessibilityLabel={saveLabel}
          >
            <Text style={[modalExperience.detailActionText, modalExperience.detailActionTextPrimary]}>
              {saving ? 'Guardando…' : saveLabel}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={modalExperience.detailActionBtn}
            onPress={handleClose}
            disabled={saving}
            accessibilityRole="button"
            accessibilityLabel="Cancelar"
          >
            <Text style={modalExperience.detailActionText}>Cancelar</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Pressable>
  );

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={handleClose}>
        <Pressable
          style={[styles.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : modalExperience.modalRoot, isMobile ? null : modalBackdropPadding]}
          onPress={handleClose}
        >
          {isMobile ? (
            <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap} automaticOffset>
              {card}
            </KeyboardAvoidingView>
          ) : (
            card
          )}
        </Pressable>
    </Modal>
  );
}
